import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

export interface PrivacyMeta {
    policyVersion: string;
    policyEffective: string;
    policyUrl: string;
}

export interface PrivacyContextType {
    // Privacy Meta & Region
    privacyMeta: PrivacyMeta | null;
    setPrivacyMeta: React.Dispatch<React.SetStateAction<PrivacyMeta | null>>;
    privacyRegion: string | null;
    setPrivacyRegion: React.Dispatch<React.SetStateAction<string | null>>;

    // Consents & Scopes
    privacyConsents: any[];
    setPrivacyConsents: React.Dispatch<React.SetStateAction<any[]>>;
    privacyMissingScopes: string[];
    setPrivacyMissingScopes: React.Dispatch<React.SetStateAction<string[]>>;

    // Consent Flow
    sessionConsentChecked: boolean;
    setSessionConsentChecked: React.Dispatch<React.SetStateAction<boolean>>;
    pendingConsentAcceptance: boolean;
    setPendingConsentAcceptance: React.Dispatch<React.SetStateAction<boolean>>;
    isPrivacySubmitting: boolean;
    setIsPrivacySubmitting: React.Dispatch<React.SetStateAction<boolean>>;

    // Sync Status
    isSyncing: Record<string, boolean>;
    setIsSyncing: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

interface PrivacyProviderProps {
    children: ReactNode;
}

export const PrivacyProvider: React.FC<PrivacyProviderProps> = ({ children }) => {
    // Privacy Meta & Region
    const [privacyMeta, setPrivacyMeta] = useState<PrivacyMeta | null>(null);
    const [privacyRegion, setPrivacyRegion] = useState<string | null>(null);

    // Consents & Scopes
    const [privacyConsents, setPrivacyConsents] = useState<any[]>([]);
    const [privacyMissingScopes, setPrivacyMissingScopes] = useState<string[]>([]);

    // Consent Flow
    const [sessionConsentChecked, setSessionConsentChecked] = useState(false);
    const [pendingConsentAcceptance, setPendingConsentAcceptance] = useState(false);
    const [isPrivacySubmitting, setIsPrivacySubmitting] = useState(false);

    // Sync Status
    const [isSyncing, setIsSyncing] = useState<Record<string, boolean>>({});

    const value: PrivacyContextType = useMemo(() => ({
        privacyMeta,
        setPrivacyMeta,
        privacyRegion,
        setPrivacyRegion,
        privacyConsents,
        setPrivacyConsents,
        privacyMissingScopes,
        setPrivacyMissingScopes,
        sessionConsentChecked,
        setSessionConsentChecked,
        pendingConsentAcceptance,
        setPendingConsentAcceptance,
        isPrivacySubmitting,
        setIsPrivacySubmitting,
        isSyncing,
        setIsSyncing
    }), [
        privacyMeta, privacyRegion, privacyConsents, privacyMissingScopes,
        sessionConsentChecked, pendingConsentAcceptance, isPrivacySubmitting, isSyncing
    ]);

    return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
};

export const usePrivacy = (): PrivacyContextType => {
    const context = useContext(PrivacyContext);
    if (context === undefined) {
        throw new Error('usePrivacy must be used within a PrivacyProvider');
    }
    return context;
};

export default PrivacyContext;
